import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, EMPTY, map, Observable } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { DataBindingSourceType, getActiveBinding } from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { DatasetRow } from '@blaze-shared/schema-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';

import { RuntimeFacade } from '../../../../+state/runtime/runtime.facade';
import { ElementDefinitionsService } from '../../../../services/element-definitions.service';
import { hasDataProps } from '../../../element-model/element-props-guards';
import { DataProviderQuery, InputDataPackage } from '../../io-data.models';

import { DataSourceProvider } from './data-source.data-provider';

@Injectable({ providedIn: 'root' })
export class MirrorDataProvider implements DataSourceProvider {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  readonly sourceType: DataBindingSourceType = 'mirror';

  getDataStream({ source }: DataProviderQuery): Observable<InputDataPackage> {
    const elementType = this.runtimeFacade.elementsValue[source.id]?.type;
    if (!elementType) {
      return EMPTY;
    }

    return this.store
      .select(
        runtimeSelectors.selectOutputUiDataById(
          source.id,
          this.elementDefinitionsService.getDefaultOutputEventType(elementType),
        ),
      )
      .pipe(
        map((rawData) => {
          const targetElement = this.runtimeFacade.elementsValue[source.id];
          const targetSource = hasDataProps(targetElement)
            ? getActiveBinding(targetElement.props.data)?.dataSource
            : undefined;
          const data = Array.isArray(rawData)
            ? rawData.filter((row: DatasetRow) => !row._isDeleted)
            : rawData;
          return {
            sourceType: this.sourceType,
            data,
            schema:
              targetSource &&
              this.schemaService.getSchema(targetSource.id, targetSource.sourceType === 'context'),
          };
        }),
        distinctUntilChanged(isDeepEqual),
      );
  }
}
